import { useMemo } from 'react';

import { defaultCurrencyRates, pricing } from '@core/constant';
import { useContextCurrency } from '@core/context';
import { CurrencyRateFormatter, ECurrency, EPricing } from '@core/type';
import { CurrencyRateEntity, CurrencyRatesResponse, EQueryKey } from '@core/type/api';

import { useQueryApi } from './use-query-api';

export const getCHFValueFromPricingKey = (priceKey: EPricing) => {
  return pricing[priceKey];
};

export const useCurrencyRate = (
  currencyProps?: ECurrency,
): {
  getConvertedPriceFromCHF: CurrencyRateFormatter;
  isSuccess: boolean;
} => {
  const { currency: contextCurrency } = useContextCurrency();
  const currency = currencyProps ?? contextCurrency;

  const { data: currencyRatesData, isSuccess } = useQueryApi<CurrencyRatesResponse>(
    EQueryKey.CURRENCY_RATES,
  );

  const isCurrencyCHF = currency === ECurrency.CHF;

  const rates = useMemo((): CurrencyRateEntity => {
    if (isSuccess) {
      return isCurrencyCHF
        ? defaultCurrencyRates
        : currencyRatesData?.rates.find(({ currencyIso }) => currencyIso === currency) || null;
    } else {
      return null;
    }
  }, [currency, currencyRatesData?.rates, isCurrencyCHF, isSuccess]);

  const getConvertedPriceFromCHF: CurrencyRateFormatter = (rateKey, valueInCHF) => {
    const rate = rates?.[rateKey];
    return rate ? rate * valueInCHF : null;
  };

  return { getConvertedPriceFromCHF, isSuccess };
};
